import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Alert, Button } from 'react-bootstrap';
import DashboardStats from './DashboardStats';
import TournamentSection from './TournamentSection';
import PlayerStatusTable from './PlayerStatusTable';
import TransactionTable from './TransactionTable';
import ShimmerLoader from './ShimmerLoader';
import { useRoleBasedData } from '../hooks/useRoleBasedData';
import { getDashboardData, DashboardData } from '../api/dashboard/dashboardService';
import { getClubInfo, ClubInfo } from '../api/club/clubService';
import './DashboardStyles.css';
import { getImageUrl } from '../utils/imageUrl';

const ClubManagerDashboard: React.FC = () => {
  const { getAccessibleClubIds } = useRoleBasedData();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [clubInfo, setClubInfo] = useState<ClubInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch dashboard data from API
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get club IDs for filtering
      const clubIds = getAccessibleClubIds();
      
      console.log('🔍 Dashboard Component Debug - Club IDs from hook:', clubIds);
      console.log('🔍 Dashboard Component Debug - Club IDs type:', typeof clubIds);
      console.log('🔍 Dashboard Component Debug - Club IDs length:', clubIds?.length);
      
      let data: DashboardData;
      
      // Always try to fetch from API first, regardless of club IDs
      try {
        // Use first club ID if available, otherwise use a default
        const clubId = clubIds && clubIds.length > 0 ? clubIds[0] : 'default';
        console.log('🔍 Dashboard Component Debug - Selected club ID:', clubId);
        console.log('🔍 Dashboard Component Debug - About to call getDashboardData with clubId:', clubId);
        
        data = await getDashboardData(clubId);
        
        console.log('🔍 Dashboard Component Debug - Dashboard data received:', data);
        console.log('🔍 Dashboard Component Debug - Stats data:', data.stats);
        
        // If tournaments are empty (API returned none or failed silently), keep empty data
        if ((!data.upcomingTournaments || data.upcomingTournaments.length === 0) &&
            (!data.ongoingTournaments || data.ongoingTournaments.length === 0)) {
          console.log('No tournaments data available from API');
        }
        
        // Also fetch club info for logo and title if club IDs are available
        if (clubIds && clubIds.length > 0) {
          try {
            const clubData = await getClubInfo(clubIds[0]);
            setClubInfo(clubData);
          } catch (clubError) {
            console.warn('Failed to fetch club info:', clubError);
            // Set fallback club info
            setClubInfo({
              _id: clubIds[0],
              name: 'Oranje Football Club',
              logo: '',
              emailId: 'manager@oranjeclub.com',
              managerName: 'Aniket',
              countryCode: 91,
              managerContactNum: 9876543210,
              location: 'Bengaluru',
              isActive: true
            });
          }
        } else {
          setClubInfo(null);
        }
      } catch (apiError) {
        console.error('API call failed:', apiError);
        setError('Failed to load dashboard data from API');
        // Set empty data instead of mock data
        data = {
          stats: {
            teams: 0,
            players: 0,
            tournaments: 0,
            payments: 0,
            activePlayers: 0,
            inactivePlayers: 0,
            activeTeams: 0,
            inactiveTeams: 0
          },
          upcomingTournaments: [],
          ongoingTournaments: [],
          recentTransactions: [],
          recentPlayers: []
        };
        
        // Set fallback club info if club IDs are available
        if (clubIds && clubIds.length > 0) {
          setClubInfo({
            _id: clubIds[0],
            name: 'Oranje Football Club',
            logo: '',
            emailId: 'manager@oranjeclub.com',
            managerName: 'Aniket',
            countryCode: 91,
            managerContactNum: 9876543210,
            location: 'Bengaluru',
            isActive: true
          });
        } else {
          setClubInfo(null);
        }
      }
      
      setDashboardData(data);
    } catch (err) {
      console.error('Dashboard data fetch error:', err);
      setError('Failed to load dashboard data');
      // Set empty data instead of mock data
      setDashboardData({
        stats: {
          teams: 0,
          players: 0,
          tournaments: 0,
          payments: 0,
          activePlayers: 0,
          inactivePlayers: 0,
          activeTeams: 0,
          inactiveTeams: 0
        },
        upcomingTournaments: [],
        ongoingTournaments: [],
        recentTransactions: [],
        recentPlayers: []
      });
      setClubInfo(null);
    } finally {
      setLoading(false);
    }
  };

  // Mock data function
  const getMockDashboardData = (): DashboardData => {
    return {
      stats: {
        teams: 15,
        players: 250,
        tournaments: 4,
        payments: 17,
        activePlayers: 200,
        inactivePlayers: 50,
        activeTeams: 10,
        inactiveTeams: 5
      },
      upcomingTournaments: [
        {
          _id: '1',
          name: 'SFL Football Tournament',
          sport: 'Football',
          ageCategories: ['U7', 'U10', 'U12', 'U15'],
          startDate: '2024-08-12',
          location: 'Madiwala, Bengaluru',
          status: 'upcoming',
          registeredTeams: 8,
          maxTeams: 16
        },
        {
          _id: '2',
          name: 'SFL Football Tournament',
          sport: 'Football',
          ageCategories: ['U9', 'U11', 'U13'],
          startDate: '2024-08-15',
          location: 'Koramangala, Bengaluru',
          status: 'upcoming',
          registeredTeams: 12,
          maxTeams: 20
        },
        {
          _id: '3',
          name: 'SFL Football Tournament',
          sport: 'Football',
          ageCategories: ['U8', 'U10', 'U12'],
          startDate: '2024-08-18',
          location: 'HSR Layout, Bengaluru',
          status: 'upcoming',
          registeredTeams: 6,
          maxTeams: 12
        }
      ],
      ongoingTournaments: [],
      recentTransactions: [
        {
          _id: '68a066001dc05829f33d3def',
          transactionId: 'pay_R5z1eNxlXU05RW',
          tournamentName: 'SFL Orange League R2',
          teamName: 'Payment testing',
          date: '2025-08-16T11:05:51.051Z',
          ageCategory: 'Junior Boys',
          amount: 9500,
          paymentMode: 'upi',
          status: 'paid'
        },
        {
          _id: '68a05e32574860a23222fcd4',
          transactionId: 'pay_R5ySPtifY6oNQb',
          tournamentName: 'SFL Orange League R1',
          teamName: 'YELIGAR',
          date: '2025-08-16T10:32:29.837Z',
          ageCategory: 'U8',
          amount: 3000,
          paymentMode: 'upi',
          status: 'paid'
        },
        {
          _id: '68a05e084c4e3e93f2526c01',
          transactionId: 'pay_R5yRexHwjziXDd',
          tournamentName: 'SFL Orange League R1',
          teamName: 'YELIGAR',
          date: '2025-08-16T10:31:46.682Z',
          ageCategory: 'U8',
          amount: 3000,
          paymentMode: 'upi',
          status: 'paid'
        },
        {
          _id: '68a05d89574860a23222fca6',
          transactionId: 'pay_R5yPRLVjs1N0Sj',
          tournamentName: 'SFL Orange League R1',
          teamName: 'YELIGAR',
          date: '2025-08-16T10:29:40.762Z',
          ageCategory: 'U10',
          amount: 7000,
          paymentMode: 'upi',
          status: 'paid'
        },
        {
          _id: '68a05d331dc05829f33d3cfe',
          transactionId: 'pay_R5yNwfjMjiQF6M',
          tournamentName: 'SFL Orange League R1',
          teamName: 'Codewave',
          date: '2025-08-16T10:28:16.096Z',
          ageCategory: 'U8',
          amount: 3000,
          paymentMode: 'upi',
          status: 'paid'
        }
      ],
      recentPlayers: []
    };
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <Container fluid>
        {/* Shimmer loader for club header */}
        <div className="mb-4">
          <div className="d-flex align-items-center mb-3">
            <div className="shimmer-circle" style={{ width: '48px', height: '48px', marginRight: '16px' }}></div>
            <div className="shimmer-text" style={{ width: '300px', height: '32px' }}></div>
          </div>
        </div>
        
        {/* Shimmer loader for stats */}
        <div className="mb-4">
          <ShimmerLoader type="stats" count={8} />
        </div>
        
        {/* Shimmer loader for tournaments */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="shimmer-text" style={{ width: '200px', height: '24px' }}></div>
            <div className="shimmer-text" style={{ width: '100px', height: '16px' }}></div>
          </div>
          <Row>
            {[1, 2, 3].map((index) => (
              <Col lg={4} md={6} key={index} className="mb-3">
                <ShimmerLoader type="card" height="400px" />
              </Col>
            ))}
          </Row>
        </div>
      </Container>
    );
  }

  if (error && !dashboardData) {
    return (
      <Container fluid>
        <Alert variant="danger" className="mt-3">
          <Alert.Heading>Error Loading Dashboard</Alert.Heading>
          <p>{error}</p>
          <hr />
          <Button variant="outline-danger" onClick={fetchDashboardData}>
            Try Again
          </Button>
        </Alert>
      </Container>
    );
  }

  if (!dashboardData) {
    return (
      <Container fluid>
        <Alert variant="warning" className="mt-3">
          No dashboard data available.
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid style={{ padding: '0 15px', marginTop: '20px' }}>
      {/* Club Logo and Title */}
      {clubInfo && (
        <div className="mb-4" style={{ padding: '0 15px' }}>
          <div className="d-flex align-items-center">
            {clubInfo.logo && (
              <div className="me-3">
                <img 
                  src={getImageUrl(clubInfo.logo)}
                  alt={`${clubInfo.name} Logo`}
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2px solid #e9ecef'
                  }}
                  onError={(e) => {
                    // Hide the image if it fails to load
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              </div>
            )}
            <div>
              <h2 className="mb-0 fw-bold" style={{ color: '#212529', fontSize: '1.75rem', lineHeight: '1.2' }}>
                {clubInfo.name}
              </h2>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Overview Cards */}
      <DashboardStats stats={dashboardData.stats} />

      {/* Upcoming Tournaments Section */}
      <TournamentSection
        title="Upcoming Tournaments"
        tournaments={dashboardData.upcomingTournaments}
        type="upcoming"
        viewAllLink="/tournaments"
      />

      {/* Recent Transactions Section */}
      <Row className="mb-4">
        <Col lg={12}>
          <TransactionTable
            title="Latest Transactions"
            transactions={dashboardData.recentTransactions}
            viewAllLink="/payment-transactions/transactions"
            maxRows={5}
            isDashboard={true}
          />
        </Col>
      </Row>

      {/* Recent Players Status Section */}
      <Row>
        <Col lg={12}>
          <PlayerStatusTable
            players={dashboardData.recentPlayers}
            viewAllLink="/my-club/player-information"
          />
        </Col>
      </Row>

      {/* Tournament Sections - Commented out as requested */}
      {/* <Row>
        <Col lg={8}>
          <TournamentSection
            title="Ongoing Tournaments"
            tournaments={dashboardData.ongoingTournaments}
            type="ongoing"
            viewAllLink="/tournaments"
          />
        </Col>
        
        <Col lg={4}>
          <Row>
            <Col lg={12} className="mb-4">
              <TransactionTable
                transactions={dashboardData.recentTransactions}
                viewAllLink="/payment-transactions/transactions"
                maxRows={5}
                isDashboard={true}
              />
            </Col>
          </Row>
        </Col>
      </Row> */}

      {/* Recent Players Status - Commented out as requested */}
      {/* <Row>
        <Col lg={12}>
          <PlayerStatusTable
            players={dashboardData.recentPlayers}
            viewAllLink="/my-club/player-information"
          />
        </Col>
      </Row> */}
    </Container>
  );
};

export default ClubManagerDashboard; 